import { useState, useEffect, useCallback } from 'react'
import { FileText, Plus, Trash2, Save, X, Power } from 'lucide-react'
import { toast } from 'sonner'
import { part11Service as svc, OrgTemplate, TemplateKind } from '../services/part11'
import { getErrorMessage } from '../utils/apiTransformers'

const KIND_LABELS: Record<TemplateKind, string> = {
  project: 'Project',
  task: 'Task',
  status: 'Status set',
  evidence: 'Evidence',
  custom_field: 'Custom field',
}

const KINDS: TemplateKind[] = ['project', 'task', 'status', 'evidence', 'custom_field']

const STARTER_PAYLOAD: Record<TemplateKind, string> = {
  project: JSON.stringify({ milestones: [], task_categories: [], kpis: [] }, null, 2),
  task: JSON.stringify({ title: '', description: '', checklist: [] }, null, 2),
  status: JSON.stringify({ statuses: [{ key: '', label: '', color: '' }] }, null, 2),
  evidence: JSON.stringify({ requirements: [{ type: '', label: '', required: true }] }, null, 2),
  custom_field: JSON.stringify({ key: '', label: '', field_type: 'text', options: [] }, null, 2),
}

export default function TemplateAdmin() {
  const [kind, setKind] = useState<TemplateKind>('project')
  const [templates, setTemplates] = useState<OrgTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<OrgTemplate | 'new' | null>(null)

  const load = useCallback(() => {
    setLoading(true)
    svc.listTemplates(kind, true)
      .then(setTemplates)
      .catch(e => toast.error(getErrorMessage(e)))
      .finally(() => setLoading(false))
  }, [kind])

  useEffect(() => { load() }, [load])

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Template Editor</h1>
        <p className="text-gray-500">
          Build reusable templates your whole organization can apply — project blueprints,
          task checklists, status sets, evidence requirements, and custom fields. No code required.
        </p>
      </div>

      <div className="flex gap-1 border-b border-gray-200 mb-6">
        {KINDS.map(k => (
          <button
            key={k}
            onClick={() => { setKind(k); setEditing(null) }}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              kind === k ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <FileText className="w-4 h-4" /> {KIND_LABELS[k]}
          </button>
        ))}
      </div>

      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-gray-800">{KIND_LABELS[kind]} templates</h2>
        <button
          onClick={() => setEditing('new')}
          className="flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" /> New template
        </button>
      </div>

      {editing && (
        <TemplateForm
          kind={kind}
          existing={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load() }}
        />
      )}

      {loading ? (
        <p className="text-gray-500 text-sm">Loading…</p>
      ) : templates.length === 0 ? (
        <p className="text-gray-500 text-sm">No {KIND_LABELS[kind].toLowerCase()} templates yet.</p>
      ) : (
        <div className="space-y-2">
          {templates.map(t => (
            <div key={t.id} className="flex items-center justify-between border border-gray-200 rounded-md p-3 bg-white">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900 truncate">{t.name}</span>
                  {!t.is_active && (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">inactive</span>
                  )}
                </div>
                {t.description && <p className="text-sm text-gray-500 truncate">{t.description}</p>}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => setEditing(t)}
                  className="px-2 py-1 text-sm text-gray-600 hover:text-gray-900"
                >
                  Edit
                </button>
                <button
                  onClick={async () => {
                    if (!confirm(`Delete template "${t.name}"?`)) return
                    try { await svc.deleteTemplate(t.id); toast.success('Deleted'); load() }
                    catch (e) { toast.error(getErrorMessage(e)) }
                  }}
                  className="p-1 text-gray-400 hover:text-red-600"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function TemplateForm({
  kind, existing, onClose, onSaved,
}: {
  kind: TemplateKind
  existing: OrgTemplate | null
  onClose: () => void
  onSaved: () => void
}) {
  const [name, setName] = useState(existing?.name ?? '')
  const [description, setDescription] = useState(existing?.description ?? '')
  const [isActive, setIsActive] = useState(existing?.is_active ?? true)
  const [payloadText, setPayloadText] = useState(
    existing ? JSON.stringify(existing.payload, null, 2) : STARTER_PAYLOAD[kind]
  )
  const [saving, setSaving] = useState(false)

  const save = async () => {
    let payload: Record<string, unknown>
    try {
      payload = JSON.parse(payloadText || '{}')
    } catch {
      toast.error('Payload is not valid JSON')
      return
    }
    if (!name.trim()) { toast.error('Name is required'); return }
    setSaving(true)
    try {
      if (existing) {
        await svc.updateTemplate(existing.id, { name, description, payload, is_active: isActive })
      } else {
        await svc.createTemplate({ kind, name, description, payload, is_active: isActive })
      }
      toast.success('Saved')
      onSaved()
    } catch (e) {
      toast.error(getErrorMessage(e))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="border border-gray-200 rounded-md p-4 bg-gray-50 mb-4">
      <div className="flex justify-between items-center mb-3">
        <h3 className="font-semibold text-gray-800">
          {existing ? `Edit "${existing.name}"` : `New ${KIND_LABELS[kind]} template`}
        </h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
      </div>
      <div className="space-y-3">
        <input
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="Template name"
          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
        />
        <input
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="Description (optional)"
          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
        />
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Payload (JSON)</label>
          <textarea
            value={payloadText}
            onChange={e => setPayloadText(e.target.value)}
            rows={10}
            spellCheck={false}
            className="w-full px-3 py-2 border border-gray-300 rounded-md font-mono text-xs"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} />
          <Power className="w-4 h-4" /> Active
        </label>
        <div className="flex gap-2">
          <button
            onClick={save}
            disabled={saving}
            className="flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save'}
          </button>
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900">Cancel</button>
        </div>
      </div>
    </div>
  )
}
